import type { ReactNode } from 'react'

/** Mist-blue category pill — the design-language badge that sits above every section heading. */
export function Pill({ children, dot = true }: { children: ReactNode; dot?: boolean }) {
  return (
    <span className="pill">
      {dot && <span className="pill-dot" aria-hidden />}
      {children}
    </span>
  )
}

/** Pill + oversized serif heading + optional sub — the canonical section opener. */
export function SectionHeading({
  eyebrow,
  title,
  sub,
  align = 'left',
  className = '',
}: {
  eyebrow: string
  title: ReactNode
  sub?: ReactNode
  align?: 'left' | 'center'
  className?: string
}) {
  return (
    <div className={`${align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'} ${className}`}>
      <div className={align === 'center' ? 'flex justify-center' : ''}>
        <Pill>{eyebrow}</Pill>
      </div>
      <h2 className="text-band mt-5 font-display">{title}</h2>
      {sub && <p className="mt-4 text-lg leading-relaxed text-[color:rgb(var(--muted))]">{sub}</p>}
    </div>
  )
}

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
    <path d="M3 8.5l3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** Forest square check-badge + label/description — the two-column feature-grid item. */
export function FeatureItem({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex gap-4">
      <span className="feature-check mt-0.5" aria-hidden>
        <CheckIcon />
      </span>
      <div>
        <p className="font-display text-lg leading-snug">{title}</p>
        {children && <p className="mt-1.5 text-[color:rgb(var(--muted))]">{children}</p>}
      </div>
    </div>
  )
}

/** Scale-contrast stat — big number + quiet label, no dividers. */
export function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div>
      <div className="stat-num font-display text-[color:rgb(var(--brand-primary))]">{value}</div>
      <div className="label-mono mt-2 text-[color:rgb(var(--muted))]">{label}</div>
    </div>
  )
}
